/* eslint-disable @typescript-eslint/naming-convention */
// Standalone mount function for the AIChatWidget.
//
// This module is the public API surface of the `chat-widget.[hash].js` bundle
// produced by `config/webpack/chat-widget.js` (task-01). It is imported by
// `frontend/packs/chat-widget.ts`, which attaches `mountChatWidget` to
// `window` so external hosts (primarily the Technician Portal) can mount the
// React chat widget into any DOM element from a plain `<script>` tag.
//
// Responsibilities:
//   1. Configure orchestrator base URL and pre-seed the AI JWT so the first
//      orchestrator request has credentials.
//   2. Render `<AIChatWidget>` wrapped in a QueryClientProvider.
//   3. Return a cleanup callback so the host (e.g. Angular `ngOnDestroy`) can
//      unmount the React tree and clear widget-owned globals.
//
// It intentionally does NOT modify the widget's internals — the widget's
// existing token fetch / refresh logic still targets the back-office
// `/api/internal/ai/token` endpoint. Cross-origin refresh support (via the
// new `/api/technician_portal/ai/token` endpoint from task-04) is completed
// by the technician-portal integration work (tech-portal task-03).
import React, { useState, forwardRef, useImperativeHandle } from 'react';
import ReactDOM from 'react-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { configureApiClient, TECHNICIAN_PORTAL_PATH_REWRITES } from '@utils/api-client';
import { AIChatWidget } from './AIChatWidget';
import { configureAiTokenSurface } from './services/aiTokenApi';
import { setStoredAiToken } from './services/aiTokenStorage';
import { parsePageContext } from './services/pageContextSchema';
// Import the widget's own top-level styles so MiniCssExtractPlugin (configured
// in `config/webpack/chat-widget.js`) emits a sibling `chat-widget.[hash].css`
// file containing the widget's styles. Nested Quimby UI / SCSS imports within
// the widget's component tree are picked up transitively by the webpack build.
import './AIChatWidgetStyles.scss';
const MountWrapper = forwardRef(function MountWrapper({ userName, defaultOpen, presentationMode, initialContext, host, coachWelcomeSeen }, ref) {
    const [pageContext, setPageContext] = useState(initialContext);
    useImperativeHandle(ref, () => ({
        updatePageContext(ctx) {
            // Validate and drop invalid contexts — never crash the widget.
            const validated = parsePageContext(ctx);
            setPageContext(validated);
        }
    }), []);
    return React.createElement(AIChatWidget, {
        userName,
        defaultOpen,
        presentationMode,
        pageContext,
        host,
        coachWelcomeSeen
    });
});
function configureOrchestratorUrl(orchestratorBaseUrl) {
    var _a;
    if (!orchestratorBaseUrl)
        return null;
    if (typeof window === 'undefined')
        return null;
    const previous = (_a = window.REACT_AI_CHAT_API_GATEWAY_DOMAIN) !== null && _a !== void 0 ? _a : null;
    window.REACT_AI_CHAT_API_GATEWAY_DOMAIN = orchestratorBaseUrl;
    return previous;
}
function restoreOrchestratorUrl(previous) {
    if (typeof window === 'undefined')
        return;
    if (previous === null) {
        delete window.REACT_AI_CHAT_API_GATEWAY_DOMAIN;
    }
    else {
        window.REACT_AI_CHAT_API_GATEWAY_DOMAIN = previous;
    }
}
export function mountChatWidget(containerElement, options = {}) {
    var _a, _b;
    if (!containerElement) {
        throw new Error('[mountChatWidget] containerElement is required');
    }
    const queryClient = new QueryClient();
    const resolvedHost = options.host === 'technician_portal' ? 'technician_portal' : 'office';
    // Capture previous state so cleanup can restore rather than blindly delete.
    const previousOrchestratorUrl = configureOrchestratorUrl(options.orchestratorBaseUrl);
    // Redirect the Rails apiClient to the tenant origin + inject query-param
    // auth when the widget is loaded cross-origin from a non-Rails host.
    // When these options are absent (widget embedded directly in the Rails
    // app), `configureApiClient` is a no-op because both `baseUrl` and
    // `authParams` are undefined.
    //
    // In cross-origin (tech portal) mode we also remap the AI endpoints from
    // their user-facing namespace to the technician-portal namespace. The
    // widget's code still hardcodes `/api/v1/ai/chat_sessions` and
    // `/api/internal/ai/token` (which require Devise cookie auth and aren't in
    // the CORS allowlist); the `/api/technician_portal/ai/*` counterparts
    // accept `auth_token` + `username` query params, live under the same
    // controller concerns, and are CORS-allowed.
    const needsCrossOriginAuth = Boolean(options.authToken) && Boolean(options.userName);
    const restoreApiClient = configureApiClient({
        baseUrl: (_a = options.railsBaseUrl) !== null && _a !== void 0 ? _a : undefined,
        authParams: needsCrossOriginAuth
            ? { auth_token: options.authToken, username: options.userName }
            : undefined,
        pathRewrites: needsCrossOriginAuth ? TECHNICIAN_PORTAL_PATH_REWRITES : undefined
    });
    // Pre-seed the JWT into localStorage (the widget's read path). The widget's
    // `useAiToken` hook will still refresh in-flight, but the first orchestrator
    // request has credentials immediately.
    if (options.aiToken) {
        setStoredAiToken(options.aiToken, (_b = options.aiTokenExpiresAt) !== null && _b !== void 0 ? _b : null);
    }
    // The widget re-mints its own token (on open, every 13 minutes, and on 401
    // retries). Forward the host's surface on each of those so a refresh does
    // not downgrade an `ios_webview` session to Rails' `web` default.
    const restoreAiTokenSurface = configureAiTokenSurface(options.surface);
    // Note: options.onTokenRefresh is accepted but not yet wired to the
    // widget's refresh path. See mount-types.ts for rationale.
    // Stash host-injected translation overrides so the i18n utility can read
    // them when `window.I18n` is absent (cross-origin embed scenario).
    // We only set the global when the caller actually provided overrides so we
    // never stomp an existing value with `undefined`. We also capture any
    // pre-existing value so cleanup can restore it (rather than blindly delete
    // and break overlapping mounts or hosts that set their own translations
    // before mount).
    const setTranslations = Boolean(options.translationOverrides);
    const hadPreviousTranslations = typeof window !== 'undefined' && Object.prototype.hasOwnProperty.call(window, '__IP_CHAT_WIDGET_TRANSLATIONS__');
    // eslint-disable-next-line no-underscore-dangle
    const previousTranslations = typeof window !== 'undefined' ? window.__IP_CHAT_WIDGET_TRANSLATIONS__ : undefined;
    if (setTranslations && typeof window !== 'undefined') {
        // eslint-disable-next-line no-underscore-dangle
        window.__IP_CHAT_WIDGET_TRANSLATIONS__ = options.translationOverrides;
    }
    const wrapperRef = React.createRef();
    // Validate the initial pageContext option — drop invalid shapes without throwing.
    const validatedInitialContext = parsePageContext(options.pageContext);
    const element = React.createElement(QueryClientProvider, { client: queryClient }, React.createElement(MountWrapper, {
        ref: wrapperRef,
        userName: options.userName,
        defaultOpen: options.defaultOpen,
        presentationMode: options.presentationMode,
        initialContext: validatedInitialContext,
        host: resolvedHost,
        coachWelcomeSeen: options.coachWelcomeSeen
    }));
    ReactDOM.render(element, containerElement);
    if (typeof window !== 'undefined') {
        // eslint-disable-next-line no-underscore-dangle
        window.__IP_CHAT_WIDGET_MOUNTED__ = true;
    }
    return {
        cleanup() {
            ReactDOM.unmountComponentAtNode(containerElement);
            queryClient.clear();
            // If we pre-seeded the AI token on mount, clear it so the next mount
            // (or a different user in the same tab) does not inherit stale state.
            // When the caller didn't pre-seed, we leave localStorage alone — the
            // widget's own `useAiToken` hook owns that key.
            if (options.aiToken) {
                setStoredAiToken(null);
            }
            restoreOrchestratorUrl(previousOrchestratorUrl);
            restoreApiClient();
            restoreAiTokenSurface();
            if (setTranslations && typeof window !== 'undefined') {
                if (hadPreviousTranslations) {
                    // eslint-disable-next-line no-underscore-dangle
                    window.__IP_CHAT_WIDGET_TRANSLATIONS__ = previousTranslations;
                }
                else {
                    // eslint-disable-next-line no-underscore-dangle
                    delete window.__IP_CHAT_WIDGET_TRANSLATIONS__;
                }
            }
            if (typeof window !== 'undefined') {
                // eslint-disable-next-line no-underscore-dangle
                delete window.__IP_CHAT_WIDGET_MOUNTED__;
            }
        },
        updatePageContext(ctx) {
            var _a;
            (_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.updatePageContext(ctx);
        }
    };
}
